import { supabase } from '@/lib/supabase';

export interface GoldLead {
  id?: string;
  first_name: string;
  last_name: string;
  full_address: string;
  postal_code: string;
  city: string;
  phone: string;
  email: string;
  weight: number;
  karat: number;
  estimated_value: number;
  created_at?: string;
}

export interface CreateGoldLeadData {
  firstName: string;
  lastName: string;
  address: string;
  postalCode: string;
  city: string;
  phone: string;
  email: string;
  weight: number;
  karat: number;
  estimatedValue: number;
}

export const goldLeadsService = {
  /**
   * Create a new gold lead submission
   */
  async createLead(leadData: CreateGoldLeadData): Promise<{ data: GoldLead | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('gold_leads')
        .insert({
          first_name: leadData.firstName,
          last_name: leadData.lastName,
          full_address: leadData.address,
          postal_code: leadData.postalCode,
          city: leadData.city,
          phone: leadData.phone,
          email: leadData.email,
          weight: leadData.weight,
          karat: leadData.karat,
          estimated_value: leadData.estimatedValue
        })
        .select()
        .single();

      if (error) throw error;

      // Convert snake_case to camelCase
      const camelCaseData: GoldLead = {
        id: data.id,
        first_name: data.first_name,
        last_name: data.last_name,
        full_address: data.full_address,
        postal_code: data.postal_code,
        city: data.city,
        phone: data.phone,
        email: data.email,
        weight: data.weight,
        karat: data.karat,
        estimated_value: data.estimated_value,
        created_at: data.created_at
      };

      // Send admin notification email
      try {
        await fetch('/api/send-gold-lead-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            firstName: leadData.firstName,
            lastName: leadData.lastName,
            address: leadData.address,
            postalCode: leadData.postalCode,
            city: leadData.city,
            phone: leadData.phone,
            email: leadData.email,
            weight: leadData.weight,
            karat: leadData.karat,
            estimatedValue: leadData.estimatedValue
          })
        });
      } catch (emailError) {
        console.log('Admin email notification failed:', emailError);
      }

      // Send customer thank you email
      try {
        await fetch('/api/send-customer-thank-you-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            firstName: leadData.firstName,
            email: leadData.email
          })
        });
      } catch (emailError) {
        console.log('Customer thank you email failed:', emailError);
      }

      return { data: camelCaseData, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  },

  /**
   * Get all gold leads (for admin use)
   */
  async getAllLeads(): Promise<{ data: GoldLead[] | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('gold_leads')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      return { data: data || [], error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }
};